import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { BootstrapData } from "../../shared/types";
import { api } from "./api";
import { useAuth } from "./auth";

type DataContextValue = {
  data: BootstrapData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const DataContext = createContext<DataContextValue | null>(null);

export function DataProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [data, setData] = useState<BootstrapData | null>(null);
  const [loading, setLoading] = useState(Boolean(user));
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      setData(await api.bootstrap());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "数据加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) void refresh();
    else setData(null);
  }, [user]);

  const value = useMemo(() => ({ data, loading, error, refresh }), [data, loading, error]);
  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const value = useContext(DataContext);
  if (!value) throw new Error("useData 必须在 DataProvider 内使用");
  return value;
}

